/**
 * Скальпинг: частые входы на младших таймфреймах.
 *
 * На 1–15 минутах шума больше, чем сигнала, поэтому у каждой идеи —
 * фильтр направления (средняя, VWAP или сила тренда) и объём.
 */
import { above, adx, below, close, crossDown, crossUp, ema, mirror, number, rsiOf, rvol, spec, vwap, weight, } from './kit.js';
export const SCALPING_TEMPLATES = [
    {
        id: 'scalping',
        name: 'Скальпинг по EMA 9',
        icon: '⚡',
        category: 'scalping',
        popular: true,
        hint: 'Частые входы по короткому импульсу — цена пересекает EMA 9 по ходу EMA 50.',
        steps: ['Цена по нужную сторону от EMA 50', 'Цена пересекла EMA 9', 'Объём и сила'],
        keywords: ['скальпинг', 'scalping', 'ema 9'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'scalp',
            style: 'with-trend',
            timeframe: '5m',
            idea: 'На пятиминутках в сторону средней 50 цена то откатывается, то снова пересекает быструю EMA 9 — каждое пересечение даёт короткий вход.',
            why: 'В коротком тренде откаты мелкие, и возврат за быструю среднюю — простой признак продолжения.',
            fails: 'Когда пятиминутный тренд заканчивается — пересечения становятся пилой.',
            tip: 'Скальпинг требует быстрых выходов: цель и стоп близко, держать недолго.',
        },
        build: (o) => spec(o, 'Скальпинг по EMA 9', mirror((side) => ({
            context: [side.beyond(close, ema(50))],
            trigger: side.cross(close, ema(9)),
            confirmations: [weight('c1', above(rvol, number(1.2)), 60), weight('c2', above(adx, number(20)), 40)],
        })), 50),
    },
    {
        id: 'scalp-vwap',
        name: 'Скальпинг от VWAP',
        icon: '⚡',
        category: 'scalping',
        hint: 'Короткие входы от средней по объёму по ходу локального тренда.',
        steps: ['Цена по нужную сторону от EMA 50', 'Цена пересекла VWAP', 'Объём'],
        keywords: ['скальпинг', 'vwap'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'scalp',
            style: 'with-trend',
            timeframe: '5m',
            idea: 'Внутри дня VWAP — главный ориентир: по ходу тренда цена отталкивается от неё и уходит дальше.',
            why: 'Внутридневные алгоритмы и фонды ориентируются на VWAP, и около неё собираются заявки.',
            fails: 'На открытии нового дня VWAP ещё «молодая» и мало что значит.',
            tip: 'Объём — обязательный фильтр: без него пересечений слишком много.',
        },
        build: (o) => spec(o, 'Скальпинг от VWAP', mirror((side) => ({
            context: [side.beyond(close, ema(50))],
            trigger: side.cross(close, vwap),
            confirmations: [weight('c1', above(rvol, number(1.3)), 100)],
        })), 0),
    },
    /* ---------------------------------------------------------- новые */
    {
        id: 'ema-9-21',
        name: 'EMA 9/21 + VWAP',
        icon: '⚡',
        category: 'scalping',
        hint: 'Быстрая средняя пересекает медленную по ту же сторону от VWAP — короткий тренд начался.',
        steps: ['Цена по нужную сторону от VWAP', 'EMA 9 пересекла EMA 21', 'Объём и сила'],
        keywords: ['ema 9', 'ema 21', 'vwap', 'скальпинг', 'пересечение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'scalp',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'Самая популярная связка скальперов: пересечение EMA 9 и 21, подтверждённое положением цены относительно VWAP.',
            why: 'Пересечение средних даёт момент, VWAP — сторону дня: вместе они отсекают половину случайных пересечений.',
            fails: 'В узком боковике средние переплетаются, а VWAP лежит прямо посреди цены.',
            tip: 'Не открывайте сделки в первые 15–30 минут после резкой новости — там средние запаздывают.',
        },
        build: (o) => spec(o, 'EMA 9/21 + VWAP', mirror((side) => ({
            context: [side.beyond(close, vwap)],
            trigger: side.cross(ema(9), ema(21)),
            confirmations: [weight('c1', above(rvol, number(1.2)), 60), weight('c2', above(adx, number(20)), 40)],
        })), 0),
    },
    {
        id: 'micro-pullback',
        name: 'Микро-откат в сильном тренде',
        icon: '🏎',
        category: 'scalping',
        hint: 'Тренд очень силён, цена на мгновение провалилась под EMA 9 и вернулась — вход на продолжении.',
        steps: ['Тренд очень силён (ADX выше 25)', 'EMA 9 по нужную сторону от EMA 21', 'Цена снова пересекла EMA 9'],
        keywords: ['откат', 'микро', 'скальпинг', 'тренд', 'моментум'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'scalp',
            style: 'with-trend',
            timeframe: '5m',
            idea: 'В очень сильном коротком тренде откаты длятся одну-две свечи. Вход — на возврате цены за быструю среднюю.',
            why: 'Сильный тренд не даёт глубоких откатов: каждое небольшое снижение сразу выкупают.',
            fails: 'Сильный тренд может закончиться резко — последний микро-откат оказывается разворотом.',
            tip: 'Хорошо работает в первые часы после сильной новости.',
        },
        build: (o) => spec(o, 'Микро-откат в сильном тренде', mirror((side) => ({
            context: [above(adx, number(25)), side.beyond(ema(9), ema(21))],
            trigger: side.cross(close, ema(9)),
            confirmations: [weight('c1', above(rvol, number(1.2)), 60), weight('c2', side.beyond(close, vwap), 40)],
        })), 0),
    },
    {
        id: 'rsi2-scalp',
        name: 'RSI-2 на пятиминутках',
        icon: '🎯',
        category: 'scalping',
        hint: 'Идея Коннорса на младшем таймфрейме: по ходу средней 50 брать короткую панику RSI(2).',
        steps: ['Цена по нужную сторону от EMA 50', 'RSI(2) ушёл в крайность', 'Паника глубокая'],
        keywords: ['rsi 2', 'коннорс', 'скальпинг', 'откат'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'scalp',
            style: 'with-trend',
            timeframe: '5m',
            idea: 'Очень быстрый RSI с периодом 2 уходит ниже 10 на каждом мелком откате. По ходу тренда это точка покупки.',
            why: 'Короткие откаты внутри тренда быстро выкупаются; RSI(2) их чётко отмечает.',
            fails: 'Если тренд пятиминуток ломается, «откат» продолжается.',
            tip: 'Держите сделки коротко: цель — возврат выше EMA 9.',
        },
        build: (o) => spec(o, 'RSI-2 на пятиминутках', mirror((side) => ({
            context: [side.beyond(close, ema(50))],
            trigger: side.sign > 0 ? crossDown(rsiOf(2), number(10)) : crossUp(rsiOf(2), number(90)),
            confirmations: [
                weight('c1', side.sign > 0 ? below(rsiOf(2), number(5)) : above(rsiOf(2), number(95)), 60),
                weight('c2', above(adx, number(20)), 40),
            ],
        })), 0),
    },
    {
        id: 'volume-push',
        name: 'Объёмный толчок',
        icon: '💨',
        category: 'scalping',
        hint: 'Объём внезапно в 2,5 раза выше обычного по ходу цены и VWAP — короткий импульс начался.',
        steps: ['Цена по нужную сторону от VWAP', 'Цена по нужную сторону от EMA 9', 'Объём вырос в 2,5 раза'],
        keywords: ['объём', 'толчок', 'импульс', 'скальпинг'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'scalp',
            style: 'breakout',
            timeframe: '5m',
            idea: 'На младших таймфреймах всплеск объёма часто предвещает несколько свечей движения в ту же сторону.',
            why: 'Кто-то крупный зашёл рыночными заявками — остальные подхватывают.',
            fails: 'Всплеск на конце движения — это выход, а не вход.',
            tip: 'Не входите, если цена уже далеко ушла от VWAP — поезд ушёл.',
        },
        build: (o) => spec(o, 'Объёмный толчок', mirror((side) => ({
            context: [side.beyond(close, vwap), side.beyond(close, ema(9))],
            trigger: crossUp(rvol, number(2.5)),
            confirmations: [weight('c1', above(adx, number(20)), 60), weight('c2', side.beyond(ema(9), ema(21)), 40)],
        })), 0),
    },
];
//# sourceMappingURL=scalping.js.map